import { Component, Input, OnInit, effect, inject, signal, computed } from '@angular/core';
import { Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { CommonModule } from '@angular/common';
import { SharedModule } from '@app/shared/shared.module';
import { PageNavigationComponent } from '@app/shared/components/page-navigation/page-navigation.component';
import { NotesCommonFunctions } from '@app/pages/notes/util/notes-common-function';
import { CourseService } from '../services/course.service';
import { Utilities } from '@app/core/utils/utilities';
import { Router } from '@angular/router';
import { CommonFunctions } from '@app/shared/util/common-functions';
import { take } from 'rxjs/operators';
import { NotesSuccessPopupComponent } from '@app/pages/notes/notes-success-popup/notes-success-popup.component';
import { CourseConfirmationPopupComponent } from '../course-confirmation-popup/course-confirmation-popup.component';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ManageFilterComponent } from '@app/shared/components/manage-filter/manage-filter.component';
import { CourseInstanceListingDetails, CourseType } from '@app/shared/enm/course-instance.enum';
import { CourseCommonFunctions } from '../course-util/course-common-functions';
import { ManageRelatedCoursesComponent } from '../related-pathway-courses/manage-related-courses/manage-related-courses.component';
import { ImageUrls } from '@app/shared/constants/image-urls-const';
import { CourseInstanceConstants } from '../course-util/course-instance-constants';
import { LoaderService } from '@app/core/services/loader.service';
import { ClearingCheckbox, ClearingInstances, ClearingCheckboxState, ClearingAvailablePayload, ClearingInstance, SecretTariffPayload } from '../models/clearing-data.model';
import { ConstantVariables } from '@app/shared/util/constant-variables';
import { Observable } from 'rxjs';
import { SuccessOrWarningPopupComponent } from '@app/shared/components/success-or-warning-popup/success-or-warning-popup.component';
import { EasyLoadService } from '@app/shared/services/easy-load.service';
import { ReportConstants } from '@app/pages/subject-report/util/report-constants';
import { UploadReportComponent } from '@app/pages/subject-report/upload-report/upload-report.component';

@Component({
  selector: 'app-course-instance-listing-page',
  templateUrl: './course-instance-listing-page.component.html',
  styleUrl: './course-instance-listing-page.component.css',
  imports: [CommonModule, SharedModule, PageNavigationComponent, NotesSuccessPopupComponent, ManageFilterComponent, SuccessOrWarningPopupComponent]
})
export class CourseInstanceListingPageComponent implements OnInit {
  @Input('courseId') courseId!: any;
  @Input('courseTypeId') courseTypeId!: any;

  util = inject(Utilities);
  router = inject(Router);
  dialog = inject(MatDialog);
  constVars = inject(ConstantVariables);
  notesCommonFn = inject(NotesCommonFunctions);
  courseDataService = inject(CourseService);
  loaderService = inject(LoaderService);
  easyLoadService = inject(EasyLoadService);
  commonFn = inject(CommonFunctions);
  courseCommonFn = inject(CourseCommonFunctions);
  imageUrlsConst = inject(ImageUrls);
  courseInsConstants = inject(CourseInstanceConstants);
  reportConstants = inject(ReportConstants);

  // Injects value if Provider token exists (if component is opened in dialog) else returns null
  courseDetails: any = inject(MAT_DIALOG_DATA, { optional: true });
  dialogRef: any = inject(MatDialogRef<CourseInstanceListingPageComponent>, { optional: true });

  pageNumber$ = signal<number>(1);
  totalCourseInstanceCount$ = signal<number>(0);
  showCourseInstancePage$ = signal<any>('');
  instanceData$ = signal<any>(null);
  hideShowFilterOption$ = signal<boolean>(false);

  // Clearing available vars
  selectAll$ = signal<boolean>(false);
  clearingAvailableUpdatePending$ = signal<boolean>(false);
  clearingCheckboxState$ = signal<ClearingCheckboxState>(new Map());
  nonArchivedClearingInstancePresent$ = computed(() => Boolean(this.clearingCheckboxState$().size));
  disableSelectAll$ = signal<boolean>(false);

  // Ucas vars
  editingSecretUcas$ = signal<number | null>(null);
  ucasFieldInvalidSubmit$ = signal<boolean>(false);
  ucasFieldValue: { value: any, default: number | null } = { value: null, default: null };

  upDatedEvent: any;
  pageTitle = signal<string>('');
  pageUrl: any = this.router.url
  clearingPage: boolean = this.pageUrl.includes('manage-clearing') && !this.dialogRef;
  dataSource = signal<any>(null);
  fromCourseListingPage: boolean = true;
  displayedColumns: string[] = ['courseInstanceId', 'courseInstanceTitle', 'academicYear', 'detailEr', 'availability', 'status', 'idpCNCourseId', 'idpcLegacyCourseId', 'courseTypeId', 'applicableForFastlane', 'courseCodeType', 'courseCode', 'courseInstanceURL', 'menu'];

  searchInstanceFields: any = {};
  institutionDetails = JSON.parse(localStorage.getItem("institutionDetails") ?? '');
  countryId = JSON.parse(localStorage.getItem("countryDetails") ?? '')?.countryId;
  courseInstanceFilterDetails = CourseInstanceListingDetails;
  courseTypeDetails = CourseType;
  courseInstanceRefDetails: any = [];
  courseStatus = signal<any>(null);
  courseInstanceListData = signal<any>({});
  successOrWarnPopupType = signal('');
  successOrWarnMsg = signal('');

  constructor() {
    this.loadCourseInstancePage()
  }

  ngOnInit(): void {
    if (this.courseDetails) {
      this.courseId = this.courseDetails.courseId;
      this.courseTypeId = this.courseDetails.courseTypeId;
    }

    if (this.clearingPage) { // Clearing only available for general
      this.displayedColumns = ['available', 'courseInstanceTitle', 'academicYear', 'detailEr', 'availability', 'status', 'courseTitle', 'qualificationType', 'ucasCode', 'secretUcasTariff', 'menu'];
      this.courseTypeId = this.courseTypeDetails.GeneralCourseTypeId;
    }
    else if (this.courseTypeId == this.courseTypeDetails.EFLCourseTypeId) {
      this.displayedColumns = ['courseInstanceId', 'courseInstanceTitle', 'academicYear', 'availability', 'status', 'idpCNCourseId', 'idpcLegacyCourseId', 'courseTypeId', 'applicableForFastlane', 'courseCodeType', 'courseCode', 'courseInstanceURL', 'menu'];
    }

    if (!this.util.quickSearchDetails())
      this.initCourseInstancePageWithData();
  }

  initCourseInstancePageWithData(sortEvent?: Sort) {
    const erQualId = this.courseDetails?.erDialogData?.erData?.erQualificationId;
    this.courseDataService.getCourseInstanceData(this.pageNumber$(), this.courseId, sortEvent?.active, sortEvent?.direction, this.searchInstanceFields, false, erQualId, this.clearingPage)
      .pipe(take(1))
      .subscribe({
        next: (res: any) => {
          this.courseInstanceListData.set(res || {});
          this.pageTitle.set(res.courseName + ' - instance');
          this.courseStatus.set(res?.courseStatus);
          this.dataSource.set(new MatTableDataSource(res?.courseInstances));
          this.totalCourseInstanceCount$.set(res?.totalRecordCount || 0);
          this.showCourseInstancePage$.set(true);

          if (this.clearingPage)
            this.updateClearingInstanceState(res as ClearingInstances);

          if (this.util.saveCourseInstanceSuccessPopup$())
            this.showSuccessMsg();
        },
        error: (err: Error) => console.log(err.message)
      });
  }

  onUcasEdit(clearingInstance: ClearingInstance) {
    this.editingSecretUcas$.set(clearingInstance.courseInstanceId);
    this.ucasFieldValue = {
      value: clearingInstance.secretUcasTariff,
      default: clearingInstance.secretUcasTariff
    }
  }

  onUcasEditCancel() {
    this.editingSecretUcas$.set(null);
    this.ucasFieldInvalidSubmit$.set(false);
    this.ucasFieldValue = { value: null, default: null };
  }

  onUcasFieldSave() {
    if (this.isSecretUcasValid()) {
      this.ucasFieldInvalidSubmit$.set(false);
      let value = null;

      if (this.ucasFieldValue.value != null)
        value = (typeof this.ucasFieldValue.value === 'string' && this.ucasFieldValue.value.trim() === '') ? null : Number(this.ucasFieldValue.value);

      if (value === this.ucasFieldValue.default) {
        this.onUcasEditCancel();
      }
      else {
        const payload: SecretTariffPayload = {
          secretTariff: value,
          updatedBy: Number(this.util.getStoredUserDetails().userId)
        }
        const obs = this.courseDataService.updateSecretUcasTariff(this.institutionDetails?.institutionId, this.editingSecretUcas$(), payload);
        this.handleResponse(obs, 'secretUcasTariffUpdate');
      }
    }
    else {
      this.ucasFieldInvalidSubmit$.set(true);
    }
  }

  isSecretUcasValid(): boolean {
    let value = this.ucasFieldValue.value;

    if (this.ucasFieldValue.value === null)
      return true

    if (typeof this.ucasFieldValue.value === 'string' && this.ucasFieldValue.value.trim() === '')
      return true;

    if (typeof this.ucasFieldValue.value === 'string' && (this.ucasFieldValue.value.includes('+') || this.ucasFieldValue.value.includes('.')))
      return false;

    value = Number(value);  // ' 123 ' -> 123 ; '12 4' -> NaN ; 'asd' -> NaN
    if (!Number.isInteger(value))   // '12.2' -> false ; 'NaN' -> false
      return false;
    return (value >= 0 && value <= 360);
  }

  onClearingAvailableReset() {
    this.clearingCheckboxState$().forEach((box: ClearingCheckbox) => box.checked = box.default);
    this.clearingAvailableUpdatePending$.set(false);
    if (this.nonArchivedClearingInstancePresent$())
      this.onCheckBoxChange('init', null, 0);
  }

  onClearingAvailableSave() {
    const payload = this.getClearingAvailableData();
    const obs = this.courseDataService.updateClearingAvailable(this.institutionDetails?.institutionId, payload);
    this.handleResponse(obs, 'clearingAvailableUpdate');
  }

  updateClearingInstanceState(res: ClearingInstances) {
    this.pageTitle.set('Clearing course management');
    this.editingSecretUcas$.set(null);
    this.ucasFieldInvalidSubmit$.set(false);
    this.clearingAvailableUpdatePending$.set(false);
    this.clearingCheckboxState$.set(new Map());
    this.disableSelectAll$.set(res.courseInstances.every((data: any) => data.status.toLowerCase() === 'archived'))

    res.courseInstances.forEach((clearingInstance: ClearingInstance) => {
      this.clearingCheckboxState$.update((state: ClearingCheckboxState) => state.set(
        clearingInstance.courseInstanceChannelId,
        {
          id: clearingInstance.courseInstanceChannelId,
          checked: clearingInstance.available,
          default: clearingInstance.available,
          status: clearingInstance.status
        }
      ))
    })
    this.onCheckBoxChange('init', null, 0);
  }

  getClearingAvailableData(): ClearingAvailablePayload {
    const data: ClearingAvailablePayload = {
      clearingEnable: [],
      clearingDisable: [],
      updatedBy: Number(this.util.getStoredUserDetails().userId)
    }

    this.clearingCheckboxState$().values().forEach((box: ClearingCheckbox) => {
      if (box.checked != box.default)
        if (box.checked)
          data.clearingEnable.push(box.id)
        else
          data.clearingDisable.push(box.id)
    })
    return data;
  }

  handleResponse(obs: Observable<any>, from: 'clearingAvailableUpdate' | 'secretUcasTariffUpdate') {
    obs.pipe(take(1)).subscribe({
      next: (res: any) => {
        if (res?.message === "Successful response") {
          const successMsg = (from === 'clearingAvailableUpdate') ? this.constVars.clearingAvailableSuccessMsg : this.constVars.secretUcasSuccessMsg;
          this.util.saveCourseInstanceSuccessPopup$.set(successMsg);
          this.initCourseInstancePageWithData();
        }
      },
      error: (err: any) => console.log("ERROR : update clearing available\n", err)
    })
  }

  onCheckBoxChange(type: 'init' | 'selectAll' | 'box', event: { source: any, checked: boolean } | null, key: number) {
    switch (type) {
      case 'init': {
        this.selectAll$.set(this.clearingCheckboxState$().values()?.filter((data: any) => data.status.toLowerCase() !== 'archived')?.every((box: ClearingCheckbox) => box.checked));
        break;
      }
      case 'box': {
        if (event) {
          this.updateCheckBox(key, event.checked);
          this.selectAll$.set(this.clearingCheckboxState$().values()?.filter((data: any) => data.status.toLowerCase() !== 'archived')?.every((box: ClearingCheckbox) => box.checked));
        }
        break;
      }
      case 'selectAll': {
        if (event) {
          this.selectAll$.set(event.checked);
          this.clearingCheckboxState$().forEach((localKey: any) => this.updateCheckBox(localKey?.id, event.checked, localKey?.status?.toLowerCase()));
        }
        break;
      }
    }
    this.clearingAvailableUpdatePending$.set(this.clearingCheckboxState$().values().some((box: ClearingCheckbox) => box.checked != box.default));
  }

  updateCheckBox(key: number, value: boolean, status?: string) {
    const current = this.clearingCheckboxState$().get(key);
    if (current && status !== 'archived')
      this.clearingCheckboxState$.update((state: ClearingCheckboxState) => state.set(key, {
        ...current,
        checked: value
      }))
  }

  filtersApplied() {
    return Boolean(Object.keys(this.searchInstanceFields)?.length);
  }

  onTableSortedOrderChange(sortEvent: Sort) {
    this.upDatedEvent = this.commonFn.updateSortingdetails(sortEvent);
    this.pageNumber$.set(1);
    this.initCourseInstancePageWithData(this.upDatedEvent);
  }

  onAddNewNote(courseInstanceId: number) {
    this.notesCommonFn.openNotesDialog('CourseInstance', courseInstanceId);
  }

  getPaginationDetails(pageNo: number) {
    this.pageNumber$.set(pageNo);
    this.initCourseInstancePageWithData(this.upDatedEvent);
  }

  loadCourseInstancePage() {
    effect(() => {
      if (this.notesCommonFn.successPopup() === 'reload' || this.courseCommonFn.reloadInstancePage$()) {
        this.initCourseInstancePageWithData();
        this.courseCommonFn.reloadInstancePage$.set(false);
      }

      if (this.util.quickSearchDetails()?.entityType?.toLowerCase() === 'course instance') {
        const searchData = this.util.quickSearchDetails();
        this.util.quickSearchDetails.set('');
        const fieldName: any = Object.keys(searchData)?.find((key) => key !== 'entityType');
        this.searchInstanceFields = { [fieldName]: searchData?.[fieldName] };
        this.pageNumber$.set(1);
        this.initCourseInstancePageWithData();
      }
    });

    if (!this.clearingPage && (!this.pageUrl.includes('manage-course') || this.util.assignedCourseInstancePopup()))
      this.fromCourseListingPage = false;
    else
      this.fromCourseListingPage = true;
  }

  showSuccessMsg() {
    setTimeout(() => {
      this.util.saveCourseInstanceSuccessPopup$.set('');
    }, 5000);
  }

  viewNotes(instance: any) {
    let url = `/view-notes/CourseInstance/${this.courseTypeId}-${this.clearingPage ? instance.courseId : this.courseId}-${instance.courseInstanceId}`;
    if (this.clearingPage)
      url += '?mode=clearing';
    this.router.navigateByUrl(url);
  }

  openConfirmationPopup(courseInstance: any) {
    this.util.isDialogOpen$.set(true);

    const popupData = { ...courseInstance, level: 'Instance', fastlaneEnabled: courseInstance[this.constVars.applicableForFastlane] }
    if (!this.clearingPage)
      popupData.courseId = this.courseId

    const dialogRef = this.dialog.open(CourseConfirmationPopupComponent, {
      panelClass: 'course-conf-popup',
      data: popupData
    });
    dialogRef.afterClosed().subscribe((result: any) => {
      this.util.isDialogOpen$.set(false);
      if (result) {
        this.initCourseInstancePageWithData();
        this.util.saveCourseInstanceSuccessPopup$.set(result);
        this.showSuccessMsg();
      }
    });
  }

  redirectToInstanceDetails(data: any, pageType?: string) {
    const userRole = this.util.getStoredUserDetails()?.roleName;
    let navigationUrl: string;

    if (data?.status?.toLowerCase() === 'archived' || pageType?.includes('view') || userRole === 'SCC View Only')
      navigationUrl = '/view-course/'
    else
      navigationUrl = '/edit-course/'

    if (this.clearingPage)
      navigationUrl += `${this.courseTypeId}/${data.courseId}/${data.courseInstanceId}?mode=clearing&clearingInstance=${data.courseInstanceId}`;
    else
      navigationUrl += `${this.courseTypeId}/${this.courseId}/${data.courseInstanceId}`;


    if (this.dialogRef)
      this.dialogRef?.close(navigationUrl);
    else
      this.router.navigateByUrl(navigationUrl);
  }

  createNewInstance() {
    if (this.dialogRef)
      this.dialogRef?.close('newInstance');
    else
      this.router.navigateByUrl('edit-course/' + this.courseTypeId + '/' + this.courseId + '/n');
  }

  showInstanceFilterOption(fieldId: string, fieldName: string, fieldType: string) {
    if (fieldType === 'AJAX' || fieldId === this.constVars.applicableForFastlane)
      this.fetchInstanceAjaxReferenceData(fieldId, fieldName, fieldType)
    else if (fieldType === 'SELECT_BOX')
      this.fetchInstanceFilterDropdownDetails(fieldId, fieldName, fieldType)
    else
      this.showInstanceFilterDetails(fieldId, fieldName, fieldType);
  }

  fetchInstanceAjaxReferenceData(fieldId: string, fieldName: string, fieldType: string) {
    if (!this.courseInstanceRefDetails[fieldId?.toLowerCase()])
      this.courseDataService.getInstanceAjaxReferenceData(this.courseId, fieldId, this.clearingPage).subscribe({
        next: (data: any) => {
          const instanceAjaxDetails = data.referenceTypes;
          instanceAjaxDetails.forEach((ajaxDetails: any) => {
            this.courseInstanceRefDetails[ajaxDetails?.name?.toLowerCase()] = ajaxDetails.values;
            this.showInstanceFilterDetails(fieldId, fieldName, fieldType);
          })
        },
        error: (err: any) => {
          console.log('instance ajax call err', err);
        }
      })
    else
      this.showInstanceFilterDetails(fieldId, fieldName, fieldType);
  }

  showInstanceFilterDetails(fieldId: any, fieldName: any, fieldType: any) {
    this.hideShowFilterOption$.set(true);
    const instanceFilterData = {
      filterId: fieldId,
      filterType: fieldType,
      filterName: fieldName,
      referenceDetails: this.courseInstanceRefDetails[fieldId?.toLowerCase()]
    }
    this.instanceData$.set(instanceFilterData);
  }

  fetchInstanceFilterDropdownDetails(fieldId: string, fieldName: string, fieldType: string) {
    if (!this.courseInstanceRefDetails[fieldId?.toLowerCase()])
      this.courseDataService.getInstanceReferenceData().subscribe({
        next: (data: any) => {
          const instanceRefDetails = data.referenceTypes;
          this.courseInstanceRefDetails[this.courseInstanceFilterDetails.Available] = this.courseInsConstants.clearingAvailableFilterOptions;
          instanceRefDetails.forEach((refDetails: any) => {
            if (refDetails.name === this.courseInstanceFilterDetails.CourseTypeRefId)
              this.courseInstanceRefDetails[refDetails?.name?.toLowerCase() + 'id'] = refDetails.values.filter((courseTypeData: any) => courseTypeData.parentCourseTypeId === Number(this.courseTypeId));
            else if (refDetails.name === this.courseInstanceFilterDetails.AcademicYearRefId)
              this.courseInstanceRefDetails[refDetails?.name?.toLowerCase()] = refDetails.values.filter((academicYearData: any) => academicYearData.countryId === this.countryId);
            else if (refDetails.name === this.courseInstanceFilterDetails.StatusRefId)
              this.courseInstanceRefDetails[refDetails?.name?.toLowerCase()] = refDetails.values;
            else if (refDetails.name === this.courseInstanceFilterDetails.QualificationType)
              this.courseInstanceRefDetails[refDetails?.name?.toLowerCase() + 'id'] = refDetails.values;
            this.showInstanceFilterDetails(fieldId, fieldName, fieldType);
          })
        }, error: (err: any) => {
          console.log('instance ref data err', err);
        }
      })
    else
      this.showInstanceFilterDetails(fieldId, fieldName, fieldType);
  }

  clearInstanceFilter(fieldId?: string) {
    if (fieldId)
      delete this.searchInstanceFields[fieldId];
    else
      this.searchInstanceFields = {};
    this.pageNumber$.set(1);
    this.initCourseInstancePageWithData(this.upDatedEvent);
  }

  closeInstanceFilterPopup() {
    this.hideShowFilterOption$.set(false);
  }

  searchAppliedInstanceFilter(event: any) {
    this.closeInstanceFilterPopup();
    this.pageNumber$.set(1);
    if ([this.courseInstanceFilterDetails.UCASCode, this.courseInstanceFilterDetails.CourseCodeFieldId].includes(event.filterBy))
      this.searchInstanceFields[event.filterBy] = String(event.filterValue)?.toUpperCase();
    else
      this.searchInstanceFields[event.filterBy] = event.filterValue
    this.initCourseInstancePageWithData(this.upDatedEvent);
  }

  goToAvailabilityPage(instance: any) {
    let url = `course-availability/${this.courseTypeId}/`;
    if (this.clearingPage)
      url += `${instance.courseId}/${instance.courseInstanceId}?mode=clearing`;
    else
      url += `${this.courseId}/${instance.courseInstanceId}`

    this.router.navigateByUrl(url);
    if (!this.fromCourseListingPage) this.dialogRef?.close();
  }

  openRelatedCoursePopup(element: any) {
    const relatedCourseData = {
      pageName: (element.status != 'ARCHIVED') ? 'add' : 'view',
      courseId: this.courseId,
      courseInstanceId: element.courseInstanceId,
      countryId: this.countryId,
      institutionId: this.institutionDetails?.institutionId,
      courseTypeId: Number(this.courseTypeId)
    }
    this.courseCommonFn.openRelatedInstanceDialog(relatedCourseData, ManageRelatedCoursesComponent, 'rel_course_popup');
  }

  restrictStudentType(element: any) {
    let val = null;
    if (element?.studentTypeIds?.length === 1) val = this.courseInsConstants.studentTypeRefIds.find(val => val.studentTypeId == element.studentTypeIds[0]);
    this.util.studentTypeToggleRestrict$.set({ val: val, info: false, from: 'manage-course-instance' });
  }

  redirectToCourseListing() {
    if (this.courseDetails?.pageType === 'assignedErInstances') {
      this.dialogRef?.close();
      this.courseCommonFn.openCoursePopup({ ...this.courseDetails.erDialogData });
    }
    else {
      this.router.navigateByUrl('/manage-course');
    }
  }

  selectAllCheckbox() {
    return (typeof (this.searchInstanceFields[this.courseInstanceFilterDetails.Available]) === 'string' ? (!this.searchInstanceFields[this.courseInstanceFilterDetails.Available] || (this.selectAll$() && this.searchInstanceFields[this.courseInstanceFilterDetails.Available])) : this.selectAll$());
  }

  downloadClearingReport(state: 'init' | 'download') {
    if (state === 'init') {
      const userId = Number(this.util.getStoredUserDetails().userId ?? 0);
      this.easyLoadService.initDownload(this.institutionDetails?.institutionId, userId, this.reportConstants.CLEARING_REPORT).pipe(take(1)).subscribe({
        next: (res: any) => {
          this.initCourseInstancePageWithData(this.upDatedEvent);
          if (res.statusCode === 200) this.showMessage('success', 'Clearing report download successfully initiated');
          else this.showMessage('warning', 'Unable to initiate download. Please try again later');
        },
        error: () => this.showMessage('warning', 'Unable to initiate download. Please try again later')
      });
    } else if (state === 'download') {
      this.clearingDownloadReportPreSignedUrl();
    }
  }

  showMessage(popType?: string, message?: string) {
    this.successOrWarnPopupType.set(popType ?? '');
    this.successOrWarnMsg.set(message ?? '');
    if (message)
      setTimeout(() => {
        this.showMessage();
      }, 5000);
  }

  clearingDownloadReportPreSignedUrl() {
    const fileName = this.courseInstanceListData()?.fileName || "";
    this.easyLoadService.getDownloadReportPreSignedUrl(fileName, this.reportConstants.CLEARING_REPORT)?.subscribe({
      next: (res: any) => {
        this.loaderService.forceCloseOverlay();
        const preSignedUrl = res?.download_url;
        if (preSignedUrl)
          this.util.downloadFile(preSignedUrl, fileName);
      },
      error: (err: Error) => console.log('ERROR : Error getting clearing download presigned url', err)
    })
  }

  getAvailabilityTooltip(element: any): string {
    const count = element.availabilityActiveCount || 0;
    return count === 0 ? 'No active availability assigned to this course instance.' :
      count === 1 ? 'This course instance has 1 availability.' :
        `This course instance has ${count} availabilities.`;
  }

  redirectToDetailedER(element: any) {
    this.util.courseDetailsForER$.set({
      courseType: this.clearingPage ? element?.courseTypeId : this.courseTypeId,
      courseId: this.clearingPage ? element?.courseId : this.courseId,
      courseTitle: this.clearingPage ? element?.courseTitle : this.courseInstanceListData()?.courseName,
      instanceId: element?.courseInstanceId,
      instanceTitle: element?.courseInstanceTitle,
      academicYear: element?.academicYear,
      pageType: this.clearingPage ? '/manage-clearing' : '/manage-course/instance',
      courseQualification: this.clearingPage ? element?.qualificationType : this.courseInstanceListData()?.qualification
    });
    this.courseCommonFn.redirectToDetailedER(this.clearingPage ? 'manageClearing' : 'manageInstance', this.clearingPage ? element?.courseId : this.courseId, element?.courseInstanceId);
  }

  downloadStagesModulesReport(state: 'init' | 'download') {
    if (state === 'init')
      this.courseCommonFn.downloadStagesModulesReport(this.institutionDetails?.institutionId).subscribe({
        next: (res: any) => {
          this.initCourseInstancePageWithData(this.upDatedEvent);
          if (res.statusCode === 200) this.showMessage('success', 'The modules report has been successfully initiated.');
          else this.showMessage('warning', 'Unable to initiate download. Please try again later');
        },
        error: () => this.showMessage('warning', 'Unable to initiate download. Please try again later')
      });
    else if (state === 'download')
      this.easyLoadService.getDownloadReportPreSignedUrl(this.courseInstanceListData()?.fileName ?? "", this.reportConstants.STAGES_MODULE_REPORT)?.subscribe({
        next: (res: any) => {
          this.loaderService.forceCloseOverlay();
          const preSignedUrl = res?.download_url;
          if (preSignedUrl) {
            this.util.downloadFile(preSignedUrl, this.courseInstanceListData()?.fileName);
            this.showMessage('success', 'The modules report has been successfully downloaded.');
          }
        },
        error: (err: Error) => console.log('ERROR : Error getting download presigned url', err)
      });
  }

  openUploadPopup() {
    this.courseCommonFn.openUploadPopup(this.institutionDetails?.institutionId)?.subscribe((uploadResponse: any) => {
      this.util.isDialogOpen$.set(false);
      if (uploadResponse) {
        this.showMessage('success', 'Modules report is successfully uploaded.');
        this.initCourseInstancePageWithData(this.upDatedEvent);
      }
    });
  }

  openClearingUploadPopup() {
    this.util.isDialogOpen$.set(true);
    const dialogRef = this.dialog.open(UploadReportComponent, {
      panelClass: 'upload_report',
      width: '512px',
      data: { fromPage: 'clearing', institutionId: this.institutionDetails?.institutionId }
    });
    dialogRef.afterClosed().subscribe(result => {
      this.util.isDialogOpen$.set(false);
      if (result) {
        this.showMessage('success', result);
        this.initCourseInstancePageWithData(this.upDatedEvent);
      }
    });
  }
}